import {
	agedCarePlanning,
	businessControl,
	businessSaleProceedsInvesting,
	commodities,
	enduringGuardianship,
	enduringPowerOfAttorney,
	familyCharter,
	hkLabel,
	holisticWealthManagement,
	implementationAndAdministrationOfEstatePlans,
	intergenerationalWealthPlan,
	investmentRiskDiversification,
	noneOfAboveOptionValue,
	philanthropicLegacies,
	retirementPreparedness,
	smsfControl,
	ukLabel,
	usLabel
} from '../../../core/constants';
import { UserOfficeCountries } from '../../../core/enums';
import { getLocalizedWord } from '../../../lib/utils';
import { DropDownListOption } from '../../../models/DropDownListOption';

const countries: string[] = [
	UserOfficeCountries.AU,
	'Austria',
	'Belarus',
	'China',
	'Cyprus',
	'England',
	'France',
	'Germany',
	hkLabel,
	'Israel',
	'Italy',
	'Japan',
	'Malaysia',
	'Malta',
	'Netherlands',
	'New Zealand',
	'Norway',
	'Philippines',
	'Qatar',
	'Singapore',
	'South Africa',
	'South Korea',
	'Sri Lanka',
	'Switzerland',
	'Thailand',
	'United Arab Emirates',
	ukLabel,
	usLabel,
	'Viet Nam'
];

export const generateCountriesData = (): DropDownListOption<string>[] => {
	return countries.map((country) => {
		// Only need to manually map for countries that label and value are different, for others we can just use the country name for both value and label.
		// This is to ensure the value is consistent with the country value in offices table in db for API to work correctly
		if (country === usLabel) return { text: usLabel, value: UserOfficeCountries.US };
		return { text: country, value: country };
	});
};

export const auStates: DropDownListOption<string>[] = [
	{ text: 'Australian Capital Territory', value: 'ACT' },
	{ text: 'New South Wales', value: 'NSW' },
	{ text: 'Northern Territory', value: 'NT' },
	{ text: 'Queensland', value: 'QLD' },
	{ text: 'South Australia', value: 'SA' },
	{ text: 'Tasmania', value: 'TAS' },
	{ text: 'Victoria', value: 'VIC' },
	{ text: 'Western Australia', value: 'WA' }
];

export const usaStates: DropDownListOption<string>[] = [
	{ text: 'Alabama', value: 'AL' },
	{ text: 'Alaska', value: 'AK' },
	{ text: 'Arizona', value: 'AZ' },
	{ text: 'Arkansas', value: 'AR' },
	{ text: 'California', value: 'CA' },
	{ text: 'Colorado', value: 'CO' },
	{ text: 'Connecticut', value: 'CT' },
	{ text: 'Delaware', value: 'DE' },
	{ text: 'Florida', value: 'FL' },
	{ text: 'Georgia', value: 'GA' },
	{ text: 'Hawaii', value: 'HI' },
	{ text: 'Idaho', value: 'ID' },
	{ text: 'Illinois', value: 'IL' },
	{ text: 'Indiana', value: 'IN' },
	{ text: 'Iowa', value: 'IA' },
	{ text: 'Kansas', value: 'KS' },
	{ text: 'Kentucky', value: 'KY' },
	{ text: 'Louisiana', value: 'LA' },
	{ text: 'Maine', value: 'ME' },
	{ text: 'Maryland', value: 'MD' },
	{ text: 'Massachusetts', value: 'MA' },
	{ text: 'Michigan', value: 'MI' },
	{ text: 'Minnesota', value: 'MN' },
	{ text: 'Mississippi', value: 'MS' },
	{ text: 'Missouri', value: 'MO' },
	{ text: 'Montana', value: 'MT' },
	{ text: 'Nebraska', value: 'NE' },
	{ text: 'Nevada', value: 'NV' },
	{ text: 'New Hampshire', value: 'NH' },
	{ text: 'New Jersey', value: 'NJ' },
	{ text: 'New Mexico', value: 'NM' },
	{ text: 'New York', value: 'NY' },
	{ text: 'North Carolina', value: 'NC' },
	{ text: 'North Dakota', value: 'ND' },
	{ text: 'Ohio', value: 'OH' },
	{ text: 'Oklahoma', value: 'OK' },
	{ text: 'Oregon', value: 'OR' },
	{ text: 'Pennsylvania', value: 'PA' },
	{ text: 'Rhode Island', value: 'RI' },
	{ text: 'South Carolina', value: 'SC' },
	{ text: 'South Dakota', value: 'SD' },
	{ text: 'Tennessee', value: 'TN' },
	{ text: 'Texas', value: 'TX' },
	{ text: 'Utah', value: 'UT' },
	{ text: 'Vermont', value: 'VT' },
	{ text: 'Virginia', value: 'VA' },
	{ text: 'Washington', value: 'WA' },
	{ text: 'West Virginia', value: 'WV' },
	{ text: 'Wisconsin', value: 'WI' },
	{ text: 'Wyoming', value: 'WY' },
	{ text: 'District of Columbia', value: 'DC' },
	{ text: 'Puerto Rico', value: 'PR' }
];

export const getStatesByCountry = (country: string) =>
	country?.toLowerCase() === UserOfficeCountries.US.toLowerCase() ? usaStates : auStates;

export const personalInterests: string[] = [
	'Art collecting',
	'Fine Dining',
	'Real Estate Investment',
	'Travel',
	'Wine and Spirits',
	'Private Aviation',
	'Boating/Yachting',
	'Cars / Racing',
	'Watching / playing sports',
	'Golf',
	'Jewelry and Watches',
	'Fashion',
	'Technology',
	'Music and Performing Arts',
	'Wellness and Fitness',
	'Hunting and Fishing',
	'Philanthropy',
	'Investing',
	'Business'
];

export const genders: string[] = ['Male', 'Female', 'Unspecified'];

export const maritalStatuses: string[] = [
	'Single',
	'Married',
	'In a de facto relationship',
	'Separated',
	'Divorced',
	'Widowed'
];

// TODO: Need to update this for future US version app
export const residentStatuses: string[] = [`${getLocalizedWord('origin')} resident`, 'Foreign resident'];

// Should keep the same as question Occupation Options in BE Service Layer
export const occupations: string[] = [
	'Accountant',
	'Actor',
	'Acupuncturist',
	'Administrator',
	'Advertising',
	'Aeroplane Pilot',
	'Aged Care Nurse',
	'Analyst',
	'Architect',
	'Artist',
	'Auditor',
	'Barista',
	'Beautician',
	'Broker',
	'Builder',
	'Business Analyst',
	'Carpenter',
	'Chef',
	'Chemist',
	'Chief Executive Officer',
	'Chief Information Officer',
	'Child Care Centre Director',
	'Chiropractor',
	'Clinical Psychologist',
	'Computing Professional',
	'Construction Worker',
	'Consultant',
	'Counsellor',
	'Dentist',
	'Designer',
	'Director',
	'Engineer',
	'Executive',
	'Farmer/Farm Overseer',
	'Fitness Centre Manager',
	'Graphic Designer',
	'Hairdresser',
	'Horse Breeder',
	'Insurance Broker',
	'Lawyer',
	'Manager',
	'Medical Practitioner',
	'Musician',
	'Nurse',
	'Office Administrator',
	'Pharmacist',
	'Physiotherapist',
	'Plumber',
	'Programmer',
	'Project Builder',
	'Psychiatrist',
	'Psychologist',
	'Public Servant',
	'Retiree',
	'Real Estate Agent',
	'Sales Representative',
	'School Teacher',
	'Scientist',
	'Sportsperson',
	'Surgeon',
	'Teacher',
	'Therapist',
	'Wholesaler'
];

export const familyRelationships: string[] = [
	'Parent',
	'Spouse',
	'Wife',
	'Husband',
	'Child',
	'Daughter',
	'Son',
	'Sibling',
	'Grandparents',
	'Grandchild',
	'Family'
];

export const childrenRelationships: string[] = ['Child', 'Daughter', 'Son'];

export const entityTypes: string[] = [
	'Sole Trader',
	'Partnership',
	'Company',
	'Trustee Company',
	'Discretionary Trust',
	'Unit Trust',
	'SMSF',
	'Other'
];

export const powerOfAttorneyTypes: string[] = ['No', 'Enduring', 'Other'];

export const debtPositionReviewedDurations: string[] = ['<2 years', '>2years'];

export const getDebtPositionReviewedDurations = (): DropDownListOption<string>[] => {
	return debtPositionReviewedDurations.sort().map((x) => {
		return { text: x, value: x };
	});
};

export const getPersonalInterestsData = (): DropDownListOption<string>[] => {
	return personalInterests.sort().map((x) => {
		return { text: x, value: x };
	});
};

export const getGendersData = (): DropDownListOption<string>[] => {
	return genders.map((x) => {
		return { text: x, value: x };
	});
};

export const getMaritalStatusesData = (): DropDownListOption<string>[] => {
	return maritalStatuses.map((x) => {
		return { text: x, value: x };
	});
};

export const getResidentStatusesData = (): DropDownListOption<string>[] => {
	return residentStatuses.map((x) => {
		return { text: x, value: x };
	});
};

export const getOccupationsData = (): DropDownListOption<string>[] => {
	return occupations.sort().map((x) => {
		return { text: x, value: x };
	});
};

export const getRelationshipsData = (): DropDownListOption<string>[] => {
	return familyRelationships.map((x) => {
		return { text: x, value: x };
	});
};

export const getEntityTypesData = (): DropDownListOption<string>[] => {
	return entityTypes.map((x) => {
		return { text: x, value: x };
	});
};

export const getPowerOfAttorneyTypesData = (): DropDownListOption<string>[] => {
	return powerOfAttorneyTypes.map((x) => {
		return { text: x, value: x };
	});
};

export const getBusinessIndustriesData = (): DropDownListOption<string>[] => {
	return businessIndustries.sort().map((x) => {
		return { text: x, value: x };
	});
};

export interface Industry {
	name: string;
}

export interface IndustryAndSubCategory extends Industry {
	subCategories: IndustrySubCategory[];
}

export interface IndustrySubCategory {
	name: string;
}

export const industryAndSubCategories: IndustryAndSubCategory[] = [
	{ name: 'Accommodation & Food Services', subCategories: [{ name: 'Accommodation' }, { name: 'Food & Beverage' }] },
	{ name: 'Agriculture, Forestry and Fishing', subCategories: [{ name: 'Agriculture' }] },
	{
		name: 'Arts, Entertainment & Recreation',
		subCategories: [{ name: 'Creative & Performing Arts' }, { name: 'Recreation Activities' }]
	},
	{
		name: 'Construction',
		subCategories: [
			{ name: 'Building Construction' },
			{ name: 'Construction Services' },
			{ name: 'Engineering' },
			{ name: 'Property Advisory' }
		]
	},
	{ name: 'Education & Training', subCategories: [{ name: 'Education' }] },
	{
		name: 'Electricity & Utilities',
		subCategories: [
			{ name: 'Energy Supply' },
			{ name: 'Gas Supply' },
			{ name: 'Water Supply & Sewerage' },
			{ name: 'Waste Collection & Disposal' }
		]
	},
	{
		name: 'Financial & Insurance Services',
		subCategories: [{ name: 'Finance & Banking' }, { name: 'Insurance & Superannuation Funds' }]
	},
	{ name: 'Franchise', subCategories: [] },
	{
		name: 'Healthcare & Social Assistance',
		subCategories: [
			{ name: 'Health Care Services' },
			{ name: 'Medical Services' },
			{ name: 'Residential Care Services' },
			{ name: 'Social Assistance Services' }
		]
	},
	{
		name: 'IT, Media & Telecommunications',
		subCategories: [{ name: 'Publishing' }, { name: 'IT Services' }, { name: 'Telecommunications Services' }]
	},
	{
		name: 'Manufacturing',
		subCategories: [
			{ name: 'Chemical Manufacturing' },
			{ name: 'Construction' },
			{ name: 'Food Products' },
			{ name: 'Machinery and Equipment' },
			{ name: 'Transport Equipment' },
			{ name: 'Other Manufacturing' }
		]
	},
	{ name: 'Not for Profit', subCategories: [] },
	{
		name: 'Other Services',
		subCategories: [{ name: 'Personal & Other Services' }, { name: 'Repair & Maintenance' }, { name: 'Other' }]
	},
	{
		name: 'Professional  Services',
		subCategories: [
			{ name: 'Consulting Services' },
			{ name: 'Legal Services' },
			{ name: 'Marketing & Communications' },
			{ name: 'PR & Advertising' }
		]
	},
	{
		name: 'Real Estate',
		subCategories: [{ name: 'Real Estate' }, { name: 'Rental & Hiring Services' }, { name: 'Strata' }]
	},
	{
		name: 'Retail Trade',
		subCategories: [
			{ name: 'Apparel' },
			{ name: 'Food Retailing' },
			{ name: 'Motor Vehicle Retailing' },
			{ name: 'Other Store-Based Retailing' }
		]
	},
	{
		name: 'Transport',
		subCategories: [{ name: 'Rail Transport' }, { name: 'Road Transport' }, { name: 'Transport Support Services' }]
	},
	{
		name: 'Wholesale Trade',
		subCategories: [
			{ name: 'Apparel' },
			{ name: 'Goods Wholesaling' },
			{ name: 'Machinery & Equipment' },
			{ name: 'Motor Vehicle Wholesaling' }
		]
	}
];

export const hasNoSubCategories = (industryName: string) =>
	industryAndSubCategories.find((x) => x.name === industryName)?.subCategories.length === 0;

// Should keep the same as question InvestmentClassesOrAssets Options in Service Layer
export const investmentAssetsData: DropDownListOption<string>[] = [
	{ value: 'termDeposits', text: 'Term Deposits' },
	{ value: 'australianShares', text: '(Direct) Australian Shares' },
	{ value: 'internationalShares', text: '(Direct) International Shares' },
	{ value: 'directBonds', text: '(Direct) Bonds' },
	{ value: 'managedFunds', text: 'Managed Funds' },
	{ value: 'ETFs', text: 'Exchange Traded Funds (ETFs)' },
	{ value: 'REITs', text: 'Real Estate Investment Trusts (REITs)' },
	{ value: 'investmentProperty', text: 'Investment Property (non-residential)' },
	{ value: 'commercialProperty', text: '(Direct) Commercial Property' },
	{ value: commodities, text: 'Commodities' },
	{ value: 'alternatives', text: 'Alternatives' },
	{ value: 'privateEquity', text: 'Private Equity' },
	{ value: noneOfAboveOptionValue, text: 'None of the above' }
];

export const documentedWealthPlans: DropDownListOption<string>[] = [
	{ value: 'debtManagement', text: 'Debt management' },
	{ value: 'surplusSavingOrInvesting', text: 'Surplus saving/investing' },
	{ value: 'superContributions', text: 'Super contributions' },
	{ value: investmentRiskDiversification, text: 'Investment risk diversification' },
	{ value: holisticWealthManagement, text: 'Holistic wealth management' },
	{ value: retirementPreparedness, text: 'Retirement preparedness' },
	{ value: businessSaleProceedsInvesting, text: 'Business sale proceeds investing' },
	{ value: noneOfAboveOptionValue, text: 'None of the above' }
];

export const wealthReviewedInLast2Years: DropDownListOption<string>[] = [
	{ value: 'structureOrStrategy', text: 'Investments and assets allocation' },
	{ value: 'optimisation', text: 'Optimisation of superannuation and investment portfolio' },
	{ value: 'insurance', text: 'Insurance and risk management needs' },
	{ value: noneOfAboveOptionValue, text: 'None of the above' }
];

export const estatePlanningData: DropDownListOption<string>[] = [
	{ value: 'validWill', text: 'Valid will' },
	{ value: 'nominatedABeneficiaryForSuperannuation', text: 'Nominated a beneficiary for superannuation' },
	{ value: familyCharter, text: 'Family charter' },
	{ value: enduringGuardianship, text: 'Enduring Guardianship' },
	{ value: enduringPowerOfAttorney, text: 'Enduring Power of Attorney' },
	{ value: intergenerationalWealthPlan, text: 'Intergenerational wealth plan' },
	{ value: agedCarePlanning, text: 'Aged care planning' },
	{ value: noneOfAboveOptionValue, text: 'None of the above' }
];

export const legallyBindingPlansData: DropDownListOption<string>[] = [
	{ value: businessControl, text: 'Business control' },
	{ value: smsfControl, text: 'SMSF control' },
	{ value: philanthropicLegacies, text: 'Philanthropic legacies' },
	{ value: implementationAndAdministrationOfEstatePlans, text: 'Implementation and administration of estate plans' },
	{ value: 'powerOfAttorney', text: 'Power of attorney' },
	{ value: noneOfAboveOptionValue, text: 'None of the above' }
];

export const estatePlanningRequirements: DropDownListOption<string>[] = [
	{
		value: 'PreparationOfBindingNominationForSuperannuation',
		text: 'Preparation of binding nomination for Superannuation'
	},
	{
		value: 'AlteringUpdatingSelfManagedSuperannuationTrustDeed',
		text: 'Altering/updating self-managed Superannuation Trust Deed'
	},
	{
		value: 'AlteringupdatingFamilyTrustDeed',
		text: 'Altering/updating Family Trust Deed'
	},
	{
		value: 'ArrangingExtraInsuranceCoverToEnhanceEstateAssets',
		text: 'Arranging extra insurance cover to enhance estate assets'
	},
	{
		value: 'PreparingBusinessSuccessionAgreements',
		text: 'Preparing Business Succession Agreements'
	}
];

export const frequencyTypes: string[] = ['One off', 'Annual'];

export const getFrequencyTypesData = (): DropDownListOption<string>[] => {
	return frequencyTypes.sort().map((x) => {
		return { text: x, value: x };
	});
};

export const sourceOfReferral: string[] = [
	'Google Search',
	'Social media',
	'Referral/word of mouth',
	'Conference/event',
	'Email newsletter',
	'Be Better Off Show Podcast',
	'Signage',
	'Sponsorship',
	'Other'
];

export const businessIndustries: string[] = [
	'Agriculture & Farming',
	'Automotive',
	'Construction',
	'Consulting & Professional Services',
	'Education',
	'Financial Services',
	'Healthcare & Medical',
	'Hospitality & Tourism',
	'Information Technology',
	'Manufacturing',
	'Media & Entertainment',
	'Real Estate',
	'Retail & E-commerce',
	'Transportation & Logistics',
	'Trades & Services',
	'Energy & Utilities',
	'Wholesale & Distribution',
	'Nonprofit & Charities'
];
